import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { DeviceCapability, DeviceStatus } from '../../types';
import { DeviceDetectionGroupsTable } from '../detection-groups/DeviceDetectionGroupsTable';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui';

interface DeviceDetectionsDialogProps {
  canManageSuppressions: boolean;
  deviceCapabilities?: DeviceCapability[];
  deviceId: string;
  deviceSerialNumber: string;
  deviceStatus: DeviceStatus;
  isAdminUser?: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function DeviceDetectionsDialog({
  canManageSuppressions = false,
  deviceCapabilities,
  deviceId,
  deviceSerialNumber,
  deviceStatus,
  isAdminUser = false,
  onOpenChange,
}: DeviceDetectionsDialogProps) {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  useEffect(() => {
    setPage(1);
  }, [deviceId]);

  useEffect(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const { queryKey } = query;
        if (!Array.isArray(queryKey)) {
          return false;
        }

        if (queryKey[0] !== 'detection-groups') {
          return false;
        }

        const params = queryKey[1] as { deviceId?: string } | undefined;
        return params?.deviceId === deviceId;
      },
    });
  }, [deviceId, queryClient]);

  return (
    <Dialog open={true} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-8xl w-[95vw] h-[85vh] flex flex-col p-6 overflow-hidden"
        onEscapeKeyDown={(e) => {
          e.preventDefault();

          // Escape inside a field means "cancel this edit", not "close the dialog". The inline
          // suppressions section holds unsaved entry across controls that are not inputs — the
          // bandwidth/step <select>s, the badge remove buttons, the tab triggers — and it no longer
          // sits behind a nested dialog that would swallow the key, so the whole section is exempt
          // rather than just its inputs.
          //
          // This reads the FOCUSED node, which is only ever inside the section because the section
          // keeps it there: it is `tabIndex={-1}` so a click on its padding focuses the section
          // itself, and `MultiFrequencyPanel` refocuses its input when a badge's remove button
          // unmounts under the cursor. Without those, focus falls to `<body>` and Radix's
          // FocusScope pulls it up to this DialogContent — an ancestor of the section, which
          // `closest` does not match — and the dialog would close over unsaved entry.
          const { target } = e;
          if (target instanceof HTMLInputElement) return;
          if (target instanceof Element && target.closest('[data-testid="suppressions-section"]')) {
            return;
          }

          onOpenChange(false);
        }}
      >
        <DialogTitle hidden></DialogTitle>
        <DialogDescription hidden />

        <div className="flex-1 overflow-hidden min-h-0">
          <DeviceDetectionGroupsTable
            deviceId={deviceId}
            deviceSerialNumber={deviceSerialNumber}
            canManageSuppressions={canManageSuppressions}
            deviceCapabilities={deviceCapabilities}
            deviceStatus={deviceStatus}
            isAdminUser={isAdminUser}
            page={page}
            onPageChange={setPage}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
